import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {NavBar, CalendarPicker, Button, Divider, Skeleton, Tag} from 'antd-mobile';
import EmptyState from '../../../components/EmptyState';
import { DownOutline } from 'antd-mobile-icons';
import { useTranslation } from 'react-i18next';
import { getNavBarContainerStyle } from '../../../utils/deviceUtils';
import i18n, { t } from '../../../i18n';
import { TmsCompletedTaskWithWayPoints, TmsTaskWayPointWithAddressType } from '../graphql/queries/getTmsPublishedTaskByDriverByDate';
import { formatTimestampByLanguage } from '../../../utils/dateTimeUtils';
import { formatLocalizedDate } from '../../../utils/dateFormatUtils';
import {buildAddress1AndAddress2} from "../../../utils/addressUtils";
import dayjs from "dayjs";
import {TmsPath, TmsPathPre} from "../../../enums/TmsNavigatePathEnum";
import { useCompletedTmsTaskList } from '../store/transportStore';
import useHistoryControl from "../../../hooks/useHistoryControl";
import {PickupAppointmentType} from "../../pickup/graphql/types/PickupTaskWithAppointmentsType";

interface TransportFinishedPageProps {}

const TransportFinishedPage: React.FC<TransportFinishedPageProps> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { i18n } = useTranslation();
    
    // 从路由状态中获取返回的日期，如果没有则使用当前日期
    const returnedSelectedDate = location.state?.selectedDate;
    const initialDate = returnedSelectedDate ? dayjs(returnedSelectedDate).toDate() : new Date();
    const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);
    const [showCalendar, setShowCalendar] = useState(false);
    const [completedTasks, setCompletedTasks] = useState<TmsCompletedTaskWithWayPoints[]>([]);

    // 使用 store 中的查询方法
    const selectedDateStr = selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD').toString() : dayjs().format('YYYY-MM-DD').toString();
    const { completedTmsTaskList, loading, refreshCompletedTmsTaskList } = useCompletedTmsTaskList(selectedDateStr);

    useHistoryControl(TmsPath.TMS_TRANSPORTING, {
        useReplace: true,
        controlNavButton: false
    });

    // 当数据变化时更新本地状态
    useEffect(() => {
        if (completedTmsTaskList.length >= 0) {
            setCompletedTasks(completedTmsTaskList);
        }
    }, [completedTmsTaskList]);

    // 添加弱网处理：当数据为空且不在加载状态时，尝试重新获取数据
    useEffect(() => {
        refreshCompletedTmsTaskList(selectedDateStr);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDateStr]);

    // 处理从详情页返回时的日期状态更新
    useEffect(() => {
        if (returnedSelectedDate) {
            const newDate = dayjs(returnedSelectedDate).toDate();
            setSelectedDate(newDate);
        }
    }, [returnedSelectedDate]);

    // 处理日期选择
    const handleDateSelect = (date: Date | null) => {
        setSelectedDate(date);
        setShowCalendar(false);
    };

    // 计算日期范围：从两个月前开始到当天的下一天
    const getDateRange = () => {
        const today = dayjs();
        const twoMonthsAgo = today.subtract(2, 'month').startOf('month');
        const nextDay = today.add(1, 'day').endOf('day');
        return {
            min: twoMonthsAgo.toDate(),
            max: nextDay.toDate()
        };
    };

    const dateRange = getDateRange();

    const getStatusTag = (taskWithWayPoints: TmsCompletedTaskWithWayPoints) => {
        // if (taskWithWayPoints.taskStatusName === "CANCELLED") {
        //     return <Tag color="default"
        //                 className="bg-gray-200 text-gray-600">{t('pages.tms.finished.status.cancelled')}</Tag>;
        // }

        if (taskWithWayPoints.taskStatusName === "CANCELLED") {
            return <span className="text-sm font-medium text-gray-500 whitespace-nowrap">
                            {t('pages.tms.finished.status.cancelled')}</span>;
        }


        return null;
    };

    // 处理途径点点击
    const handleWayPointClick = (waypoint: TmsTaskWayPointWithAddressType, index: number, task: TmsCompletedTaskWithWayPoints) => {
        const formattedDate = selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
        console.log('TransportFinishedPage - Navigate to detail:', {
            taskWayPointId: waypoint.taskWayPointId,
            taskId: task.taskId,
            selectedDate: formattedDate,
            waypoint,
            task
        });
        // 跳转到已完成任务详情页
        navigate(TmsPathPre.TMS_TRANSPORT_FINISHED_DETAIL_PRE+ `${waypoint.taskWayPointId}/${task.taskId}/${formattedDate}`);
        return;
    };

    return (
        <div className="flex flex-col h-screen bg-gray-50">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => navigate(-1)}>
                    {t('pages.tms.finished.title')}
                </NavBar>
            </div>

            {/* 日期选择器 */}
            <div className="p-3 bg-white mb-2 shadow-sm">
                <Button
                    block
                    fill='none'
                    onClick={() => setShowCalendar(true)}
                    className="rounded-full"
                >
                    <div className="flex items-center justify-center w-full">
                        <span className="mr-1">
                            {selectedDate ? formatLocalizedDate(selectedDate) : t('pages.tms.finished.selectDate')}
                        </span>
                        <DownOutline/>
                    </div>
                </Button>
            </div>

            <CalendarPicker
                visible={showCalendar}
                onClose={() => setShowCalendar(false)}
                onConfirm={handleDateSelect}
                defaultValue={selectedDate}
                selectionMode="single"
                min={dateRange.min}
                max={dateRange.max}
                title={t('pages.tms.finished.selectDate')}
            />

            {/* 任务列表 */}
            <div className="flex-1 overflow-y-auto px-3">
                {loading ? (
                    <div className="p-4">
                        <Skeleton animated />
                        <Skeleton animated />
                        <Skeleton animated />
                    </div>
                ) : completedTasks.length === 0 ? (
                    <EmptyState />
                ) : (
                    <div className="space-y-3">
                        {completedTasks.map((task, taskIndex) => (
                            <div key={`task-${task.taskId}-${taskIndex}`} className="space-y-2">
                                {/* 任务标题 */}
                                <div className="bg-white p-2 rounded-lg">
                                    <div className="text-sm font-medium text-black text-center">
                                        {t('pages.tms.finished.taskNumber')}: {task.taskNumber}
                                    </div>
                                </div>
                                
                                {/* 任务内的途径点 */}
                                {task.wayPoints.map((waypoint, index) => (
                                    <div
                                        key={index}
                                        className="bg-white my-2 rounded-lg shadow-sm"
                                        style={{
                                            backgroundColor: task.taskStatusName === "CANCELLED"? '#e5e7eb' : '#ffffff'
                                        }}
                                        onClick={() => (waypoint.status !== 'CANCELLED') && handleWayPointClick(waypoint, index, task)}
                                    >
                                        <div className="p-4">
                                            <div className="flex justify-between items-center">
                                                <div className="text-sm font-medium truncate flex-1 mr-2">{waypoint.wayPointName}</div>
                                                {getStatusTag(task)}
                                            </div>
                                            <Divider/>
                                            <div className="flex gap-6">
                                                <div className="flex flex-col w-10 items-center justify-center gap-0.5 flex-shrink-0">
                                                    <div className="text-3xl font-bold">
                                                        {
                                                            waypoint.waypointType === 1 ?
                                                                <span
                                                                    className="text-xl whitespace-nowrap">{t('pages.tms.start')}</span>
                                                                :
                                                                waypoint.waypointType === 3 ? <span
                                                                        className="text-xl whitespace-nowrap">{t('pages.tms.end')}</span> :
                                                                    waypoint.sequence}
                                                    </div>
                                                </div>

                                                <div className="flex flex-col gap-1 flex-1 min-w-0">
                                                    <h3 className="text-sm font-semibold break-words">
                                                        {buildAddress1AndAddress2({
                                                            addressLine1: waypoint.addressLine1 ?? undefined,
                                                            addressLine2: waypoint.addressLine2 ?? undefined
                                                        })}
                                                    </h3>
                                                    <p className="text-xs">
                                                        <span className="text-gray-500">
                                                            {t('pages.tms.finished.actualArrivalTime')}：
                                                        </span>
                                                        <span className="text-[#6366f1]">
                                                            {waypoint.actualArrivalTime ? (formatTimestampByLanguage(waypoint.actualArrivalTime, i18n.language ) + (waypoint.actualArrivalTimezone ? ` (${waypoint.actualArrivalTimezone})` : '')) : '-'}
                                                        </span>
                                                    </p>
                                                    {waypoint.actualDepartureTime && (
                                                        <p className="text-xs">
                                                        <span className="text-gray-500">
                                                           {t('pages.tms.finished.actualDepartureTime')}：
                                                        </span>
                                                            <span className="text-[#6366f1]">
                                                             {waypoint.actualDepartureTime ? (formatTimestampByLanguage(waypoint.actualDepartureTime, i18n.language) + (waypoint.actualDepartureTimezone ? ` (${waypoint.actualDepartureTimezone})` : '')) : '-'}
                                                        </span>
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TransportFinishedPage;

